import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { useWizard } from "../../context/WizardContext";
import { CurrencyInput } from "../shared/CurrencyInput";
import type {
  IncomeDetail,
  Fbar,
  Fatca,
  DeductionsAndExpensesDetail,
} from "../../types/individual.types";
import { cn } from "@/lib/utils";

const ACCEPTED_TYPES = { "application/pdf": [".pdf"], "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] };
const MAX_SIZE = 10 * 1024 * 1024;

function getIncomeDetail(list: IncomeDetail[] | undefined, personType: "TAXPAYER" | "SPOUSE"): IncomeDetail | undefined {
  if (!list) return undefined;
  return list.find((d) => d.personType === personType);
}

function IncomeDetailsForm({
  personType,
  income,
  updateIncome,
  idPrefix,
}: {
  personType: "TAXPAYER" | "SPOUSE";
  income: IncomeDetail | undefined;
  updateIncome: (personType: "TAXPAYER" | "SPOUSE", patch: Partial<IncomeDetail>) => void;
  idPrefix: string;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Did you receive any interest or dividend? (1099-INT, 1099-DIV)</Label>
        <RadioGroup
          value={
            income?.didReceiveInterestOrDividend === true
              ? "yes"
              : income?.didReceiveInterestOrDividend === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateIncome(personType, { didReceiveInterestOrDividend: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-int-div-yes`} />
            <Label htmlFor={`${idPrefix}-int-div-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-int-div-no`} />
            <Label htmlFor={`${idPrefix}-int-div-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
        {(income?.didReceiveInterestOrDividend === true) && (
          <div className="grid gap-2 sm:grid-cols-2 pl-6">
            <CurrencyInput
              label="Interest (1099-INT)"
              value={income?.interestIncome}
              onChange={(v) => updateIncome(personType, { interestIncome: v })}
            />
            <CurrencyInput
              label="Dividend (1099-DIV)"
              value={income?.dividendIncome}
              onChange={(v) => updateIncome(personType, { dividendIncome: v })}
            />
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label>Did you receive any income from sale of stocks (1099-B)?</Label>
        <RadioGroup
          value={
            income?.didReceiveIncomeFromSaleOfStocks === true
              ? "yes"
              : income?.didReceiveIncomeFromSaleOfStocks === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateIncome(personType, { didReceiveIncomeFromSaleOfStocks: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-stocks-yes`} />
            <Label htmlFor={`${idPrefix}-stocks-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-stocks-no`} />
            <Label htmlFor={`${idPrefix}-stocks-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>Did you receive any refund from state/local last year? (1099-G)</Label>
        <RadioGroup
          value={
            income?.didReceiveRefundFromStateLocalLastYear === true
              ? "yes"
              : income?.didReceiveRefundFromStateLocalLastYear === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateIncome(personType, { didReceiveRefundFromStateLocalLastYear: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-refund-yes`} />
            <Label htmlFor={`${idPrefix}-refund-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-refund-no`} />
            <Label htmlFor={`${idPrefix}-refund-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>If client works contract-based income (1099-K, 1099-NEC)</Label>
        <RadioGroup
          value={
            income?.worksContractBasedIncome === true
              ? "yes"
              : income?.worksContractBasedIncome === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateIncome(personType, { worksContractBasedIncome: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-contract-yes`} />
            <Label htmlFor={`${idPrefix}-contract-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-contract-no`} />
            <Label htmlFor={`${idPrefix}-contract-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>Any other income (gambling – W-2G, bitcoin, etc.)</Label>
        <RadioGroup
          value={
            income?.anyOtherIncomeGambling === true
              ? "yes"
              : income?.anyOtherIncomeGambling === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateIncome(personType, { anyOtherIncomeGambling: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-other-inc-yes`} />
            <Label htmlFor={`${idPrefix}-other-inc-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-other-inc-no`} />
            <Label htmlFor={`${idPrefix}-other-inc-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
        {(income?.anyOtherIncomeGambling === true) && (
          <div className="pl-6">
            <CurrencyInput
              label="Amount"
              value={income?.otherIncome?.[0]?.amount}
              onChange={(v) => {
                const arr = income?.otherIncome ?? [];
                const next = [...arr];
                next[0] = { description: "Other income", amount: v };
                updateIncome(personType, { otherIncome: next });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function DeductionsForm({
  personType,
  deductions,
  updateDeductions,
  idPrefix,
}: {
  personType: "TAXPAYER" | "SPOUSE";
  deductions: Partial<DeductionsAndExpensesDetail>;
  updateDeductions: (personType: "TAXPAYER" | "SPOUSE", patch: Partial<DeductionsAndExpensesDetail>) => void;
  idPrefix: string;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Interest paid (USA / IN)</Label>
        <RadioGroup
          value={
            deductions.homeMortgageInterestPaid === true
              ? "yes"
              : deductions.homeMortgageInterestPaid === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateDeductions(personType, { homeMortgageInterestPaid: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-mortgage-yes`} />
            <Label htmlFor={`${idPrefix}-mortgage-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-mortgage-no`} />
            <Label htmlFor={`${idPrefix}-mortgage-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>Contributions to HSA (1099-SA)</Label>
        <RadioGroup
          value={
            deductions.contributionsToHSA === true
              ? "yes"
              : deductions.contributionsToHSA === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateDeductions(personType, { contributionsToHSA: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-hsa-yes`} />
            <Label htmlFor={`${idPrefix}-hsa-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-hsa-no`} />
            <Label htmlFor={`${idPrefix}-hsa-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>Contributions to IRA (1099-R)</Label>
        <RadioGroup
          value={
            deductions.contributionsToIRA === true
              ? "yes"
              : deductions.contributionsToIRA === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateDeductions(personType, { contributionsToIRA: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-ira-yes`} />
            <Label htmlFor={`${idPrefix}-ira-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-ira-no`} />
            <Label htmlFor={`${idPrefix}-ira-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>Student Loan Interest Paid</Label>
        <RadioGroup
          value={
            deductions.studentLoanInterestPaid === true
              ? "yes"
              : deductions.studentLoanInterestPaid === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateDeductions(personType, { studentLoanInterestPaid: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-loan-yes`} />
            <Label htmlFor={`${idPrefix}-loan-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-loan-no`} />
            <Label htmlFor={`${idPrefix}-loan-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>Student Tuition Fee Paid</Label>
        <RadioGroup
          value={
            deductions.studentTuitionFeePaid === true
              ? "yes"
              : deductions.studentTuitionFeePaid === false
                ? "no"
                : ""
          }
          onValueChange={(v) =>
            updateDeductions(personType, { studentTuitionFeePaid: v === "yes" })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${idPrefix}-tuition-yes`} />
            <Label htmlFor={`${idPrefix}-tuition-yes`} className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${idPrefix}-tuition-no`} />
            <Label htmlFor={`${idPrefix}-tuition-no`} className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

export function IndividualStep5OthersAndDocuments() {
  const { individualData, updateIndividualData } = useWizard();
  const hasSpouse = (individualData.personalInformation ?? []).some((p) => p.personType === "SPOUSE");
  const incomeList = individualData.incomeDetails ?? [];
  const taxpayerIncome = getIncomeDetail(incomeList, "TAXPAYER");
  const spouseIncome = getIncomeDetail(incomeList, "SPOUSE");
  const fbar = individualData.fbar ?? {};
  const fatca = individualData.fatca ?? {};

  const updateIncome = (personType: "TAXPAYER" | "SPOUSE", patch: Partial<IncomeDetail>) => {
    const rest = incomeList.filter((d) => d.personType !== personType);
    const existing = incomeList.find((d) => d.personType === personType);
    const next: IncomeDetail = { ...existing, personType, ...patch };
    updateIndividualData({ incomeDetails: [...rest, next] });
  };

  const deductionsList = individualData.deductionsAndExpenses ?? [];
  const taxpayerDeductions = deductionsList.find((d) => d.personType === "TAXPAYER") ?? {};
  const spouseDeductions = deductionsList.find((d) => d.personType === "SPOUSE") ?? {};
  const updateDeductions = (personType: "TAXPAYER" | "SPOUSE", patch: Partial<DeductionsAndExpensesDetail>) => {
    const rest = deductionsList.filter((d) => d.personType !== personType);
    const existing = deductionsList.find((d) => d.personType === personType) ?? {};
    const next: DeductionsAndExpensesDetail = { ...existing, personType, ...patch };
    updateIndividualData({ deductionsAndExpenses: [...rest, next] });
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) console.log("Files for upload:", acceptedFiles.map((f) => f.name));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_SIZE,
    multiple: true,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Others & Documents</h2>
        <p className="text-sm text-muted-foreground">
          Additional income, deductions, FBAR/FATCA, and document upload.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Income Details</CardTitle>
          <p className="text-sm text-muted-foreground">
            Yes/no plus amounts where applicable. Add for taxpayer and spouse as needed.
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="taxpayer" className="w-full">
            <TabsList className="border border-primary/30 bg-primary/5">
              <TabsTrigger value="taxpayer" className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black">
                Taxpayer
              </TabsTrigger>
              {hasSpouse && (
                <TabsTrigger value="spouse" className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black">
                  Spouse
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="taxpayer" className="mt-4 space-y-4">
              <IncomeDetailsForm
                personType="TAXPAYER"
                income={taxpayerIncome}
                updateIncome={updateIncome}
                idPrefix="tp"
              />
            </TabsContent>
            {hasSpouse && (
              <TabsContent value="spouse" className="mt-4 space-y-4">
                <IncomeDetailsForm
                  personType="SPOUSE"
                  income={spouseIncome}
                  updateIncome={updateIncome}
                  idPrefix="sp"
                />
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Other Related Expenses</CardTitle>
          <p className="text-sm text-muted-foreground">
            Deductions and expenses. Add for taxpayer and spouse as needed.
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="taxpayer" className="w-full">
            <TabsList className="border border-primary/30 bg-primary/5">
              <TabsTrigger value="taxpayer" className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black">
                Taxpayer
              </TabsTrigger>
              {hasSpouse && (
                <TabsTrigger value="spouse" className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black">
                  Spouse
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="taxpayer" className="mt-4 space-y-4">
              <DeductionsForm
                personType="TAXPAYER"
                deductions={taxpayerDeductions}
                updateDeductions={updateDeductions}
                idPrefix="tp"
              />
            </TabsContent>
            {hasSpouse && (
              <TabsContent value="spouse" className="mt-4 space-y-4">
                <DeductionsForm
                  personType="SPOUSE"
                  deductions={spouseDeductions}
                  updateDeductions={updateDeductions}
                  idPrefix="sp"
                />
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">FBAR & FATCA</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="info">
            <Info className="h-4 w-4" />
            <AlertDescription>
              FinCEN 114 (FBAR) and Form 8938 (FATCA) may apply if you have foreign accounts or assets above thresholds.
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <Label>Have you maintained or transferred funds from your personal account to another country&apos;s personal account?</Label>
            <RadioGroup
              value={fbar.hasForeignBankAccount === true ? "yes" : fbar.hasForeignBankAccount === false ? "no" : ""}
              onValueChange={(v) =>
                updateIndividualData({ fbar: { ...fbar, hasForeignBankAccount: v === "yes" } })
              }
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="fbar-yes" />
                <Label htmlFor="fbar-yes" className="font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="fbar-no" />
                <Label htmlFor="fbar-no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label>If assets exceed $50,000, you need to file FATCA.</Label>
            <RadioGroup
              value={fatca.applicable === true ? "yes" : fatca.applicable === false ? "no" : ""}
              onValueChange={(v) =>
                updateIndividualData({ fatca: { ...fatca, applicable: v === "yes" } })
              }
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="fatca-yes" />
                <Label htmlFor="fatca-yes" className="font-normal">Yes, applies to me</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="fatca-no" />
                <Label htmlFor="fatca-no" className="font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>
          <CurrencyInput
            label="Highest aggregate value (foreign accounts)"
            value={fbar.maximumAccountValue}
            onChange={(v) => updateIndividualData({ fbar: { ...fbar, maximumAccountValue: v } })}
          />
          <div className="space-y-2">
            <Label htmlFor="numForeignAccounts">Number of foreign accounts</Label>
            <Input
              id="numForeignAccounts"
              type="number"
              min={0}
              value={fbar.numberOfAccounts ?? ""}
              onChange={(e) =>
                updateIndividualData({
                  fbar: { ...fbar, numberOfAccounts: e.target.value ? Number(e.target.value) : undefined },
                })
              }
            />
          </div>
          <CurrencyInput
            label="FATCA asset value"
            value={fatca.assetValue}
            onChange={(v) => updateIndividualData({ fatca: { ...fatca, assetValue: v } })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Document Upload</CardTitle>
          <p className="text-sm text-muted-foreground">
            Please upload all relevant documents: W-2s, 1099s, 1095-A/B, foreign account statements, and any other supporting docs.
          </p>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={cn(
              "flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/30 p-8 transition-colors cursor-pointer hover:border-primary/50 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isDragActive && "border-primary bg-primary/5"
            )}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-full bg-muted p-4 text-muted-foreground">
                {isDragActive ? <FileText className="h-8 w-8" /> : <Upload className="h-8 w-8" />}
              </div>
              <p className="text-sm font-medium text-foreground">
                {isDragActive ? "Drop files here" : "Drag & drop files here, or click to browse"}
              </p>
              <p className="text-xs text-muted-foreground">PDF, JPG, PNG (max 10MB)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
